import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Circular from 'components/common/CustomCircular';
import {
  MSG_MAX_LENGTH,
  MSG_PATTERN_ALPHANUMERIC_SYMBOL,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { UnauthorizedError } from 'stores/ApiClientError';
import { login } from 'stores/AuthenticationStoreAdapter';
import { errorDefault } from 'utils/Toastify';

type Inputs = {
  userId: string;
  password: string;
};

const Login: React.FC = () => {
  const [isProgress, setProgress] = React.useState(false);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: { userId: '', password: '' },
  });

  const validationRules = {
    userId: {
      required: MSG_REQUIRED('ユーザーID'),
      maxLength: {
        value: 300,
        message: MSG_MAX_LENGTH('ユーザーID', 300),
      },
      pattern: {
        value: /^[a-zA-Z0-9!-/:-@[-`{-~]+$/,
        message: MSG_PATTERN_ALPHANUMERIC_SYMBOL('ユーザーID'),
      },
    },
    password: {
      required: MSG_REQUIRED('パスワード'),
      maxLength: {
        value: 300,
        message: MSG_MAX_LENGTH('パスワード', 300),
      },
      pattern: {
        value: /^[a-zA-Z0-9!-/:-@[-`{-~]+$/,
        message: MSG_PATTERN_ALPHANUMERIC_SYMBOL('パスワード'),
      },
    },
  };

  const handleLogin: SubmitHandler<Inputs> = async (inputs: Inputs) => {
    setProgress(true);
    try {
      await login(inputs.userId, inputs.password);
      setProgress(false);
      document.location = '/';
    } catch (error) {
      setProgress(false);
      if (error instanceof UnauthorizedError) {
        errorDefault('ログイン失敗');
      } else {
        errorDefault('想定外エラー');
        console.error(error);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: { xs: 2, md: 3 },
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #16213e 55%, #0f3460 100%)'
              : 'linear-gradient(135deg, #eef2f7 0%, #dbe4f0 50%, #c3cfe2 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
            maxWidth: 960,
            minHeight: { md: 580 },
            borderRadius: 4,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
            bgcolor: 'background.paper',
          }}
        >
          {/* 左ペイン: ウェルカム / ブランディング */}
          <Box
            sx={{
              flex: { md: '1 1 45%' },
              position: 'relative',
              p: { xs: 5, md: 6 },
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 5,
              overflow: 'hidden',
              background: 'linear-gradient(150deg, #1976d2 0%, #0f3460 100%)',
            }}
          >
            {/* 装飾的な円 */}
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -40,
                width: 180,
                height: 180,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.08)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -50,
                left: -30,
                width: 140,
                height: 140,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.06)',
              }}
            />

            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ zIndex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.16)',
                }}
              >
                <LockOutlinedIcon />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 1 }}>
                管中 / Kanzhong
              </Typography>
            </Stack>

            <Box sx={{ zIndex: 1 }}>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, lineHeight: 1.35 }}>
                おかえりなさい
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8 }}>
                社内のバックオフィス業務を、ひとつの場所で。 ログインして今日の業務を始めましょう。
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
              Ardito 社内業務管理システム
            </Typography>
          </Box>

          {/* 右ペイン: ログインフォーム */}
          <Box
            sx={{
              flex: { md: '1 1 55%' },
              p: { xs: 5, md: 6 },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              component="form"
              noValidate
              onSubmit={handleSubmit(handleLogin)}
              spacing={3}
              sx={{ width: '100%' }}
            >
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  ログイン
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  アカウント情報を入力してください
                </Typography>
              </Box>

              <Controller
                name="userId"
                control={control}
                rules={validationRules.userId}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <TextField
                      {...field}
                      type="text"
                      label="ユーザーID"
                      error={errors.userId !== undefined}
                      helperText={errors.userId?.message}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </FormControl>
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={validationRules.password}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <TextField
                      {...field}
                      type="password"
                      label="パスワード"
                      error={errors.password !== undefined}
                      helperText={errors.password?.message}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </FormControl>
                )}
              />

              <Button
                variant="contained"
                type="submit"
                fullWidth
                size="large"
                sx={{
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                ログイン
              </Button>

              <Divider sx={{ width: '100%' }}>
                <Typography variant="caption" color="text.secondary">
                  または
                </Typography>
              </Divider>

              {/* ゲスト導線: Dev Insights（公開記事）はログインなしでも閲覧できる。
                  実際の Dev Insights ヒーローのデザイン（ダークブルー / eyebrow / タグ）を
                  ミニプレビューとして再現し、「何が読めるのか」を伝える。 */}
              <Box
                onClick={() => navigate('/devdocs')}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  p: 2,
                  cursor: 'pointer',
                  color: '#fff',
                  background: 'linear-gradient(120deg, #0f172a 0%, #1e3a8a 55%, #2563eb 100%)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 24px rgba(37,99,235,0.35)',
                  },
                }}
              >
                {/* 装飾: ドットグリッド（Dev Insights ヒーローと同じテクスチャ） */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                    maskImage: 'linear-gradient(90deg, transparent 0%, #000 45%, #000 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <Stack spacing={1} sx={{ position: 'relative' }}>
                  {/* eyebrow */}
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 18,
                        height: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.6)',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.58rem',
                        fontWeight: 600,
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.7)',
                      }}
                    >
                      The Engineering Journal
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      sx={{ letterSpacing: '-0.01em' }}
                    >
                      Dev Insights
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 5,
                        bgcolor: 'rgba(255,255,255,0.14)',
                      }}
                    >
                      <MenuBookOutlinedIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={600}>
                        ログイン不要
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}
                  >
                    ゲストのまま、公開されている技術記事やナレッジを読めます。
                  </Typography>

                  {/* タグ（Dev Insights の記事カードと同じ雰囲気） */}
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {['AI', 'Workflow', 'Backend'].map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          borderRadius: 1,
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.16)',
                        }}
                      />
                    ))}
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mt: 0.5, fontWeight: 700 }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      ゲストとして記事を読む
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Circular open={isProgress} />
    </>
  );
};

export default Login;
